import { formatNumberDE } from '../../../shared/utils/formatNumber';

export const calculateSum = (shipments, key, isStock = false) => {
  return (shipments ?? []).reduce((sum, shipment) => {
    const value = isStock ? (shipment.stock?.[key] ?? 0) : (shipment[key] ?? 0);
    return sum + value;
  }, 0);
};

/**
 * Picks the most frequently occurring value. Ties are broken by picking the
 * largest value among those tied.
 */
const resolveByFrequency = (values) => {
  const frequency = new Map();
  for (const v of values) frequency.set(v, (frequency.get(v) ?? 0) + 1);

  let resolvedValue = null;
  let highestCount = 0;
  for (const [value, count] of frequency.entries()) {
    if (
      count > highestCount ||
      (count === highestCount && value > resolvedValue)
    ) {
      resolvedValue = value;
      highestCount = count;
    }
  }
  return { value: resolvedValue, count: highestCount };
};

/**
 * The First Shipping Line Offer totals for each destination should all agree
 * (the vessel capacity is duplicated across destinations, not divided).
 * Destinations where no shipment has a First Shipping Line Offer yet are
 * ignored rather than treated as a mismatching zero.
 *
 * When the destination totals disagree, the vessel capacity is resolved to
 * the most frequently occurring value (ties broken by the largest value),
 * and a note explaining the calculation is returned for display.
 */
export const resolveVesselCapacity = (destinationShipmentGroups) => {
  const values = [];
  for (const shipments of destinationShipmentGroups ?? []) {
    const hasValue = (shipments ?? []).some(
      (s) => s.firstShippingLineOffer != null
    );
    if (!hasValue) continue;
    values.push(calculateSum(shipments, 'firstShippingLineOffer'));
  }
  if (values.length === 0) return { value: null, isError: false, note: null };

  const allEqual = values.every((v) => v === values[0]);
  if (allEqual) return { value: values[0], isError: false, note: null };

  const { value, count } = resolveByFrequency(values);
  const note = `First Shipping Line Offer is inconsistent across destinations. Showing the most frequently occurring value (${formatNumberDE(
    value
  )}), found in ${count} of ${values.length} destinations.`;

  return { value, isError: true, note };
};

/**
 * Resolves the vessel capacity across a ship's destinations and returns it
 * already shaped as the `sums`-style fields consumers need, avoiding
 * repeated `{ value, isError, note }` unpacking at every call site.
 */
export const withVesselCapacity = (destinationShipmentGroups) => {
  const { value, isError, note } = resolveVesselCapacity(
    destinationShipmentGroups
  );
  return {
    firstShippingLineOffer: value,
    firstShippingLineOfferError: isError,
    firstShippingLineOfferNote: note,
  };
};
